import React, { useEffect, useRef, useState } from "react";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CheckIcon from "@mui/icons-material/Check";
import "../Members/members.css";

// Shared member-sorting control + comparator — originally built for the Members page, reused
// as-is (same component, same `.hf-mem-sort-*` classes) anywhere else a member list needs the
// identical sort options, e.g. New Appointment's "Izaberi člana" search. `field` is
// "first_name" | "last_name" | "created_at"; `dir` is "asc" | "desc".

// Serbian collation (not the default locale) so Š/Đ/Č/Ć/Ž land in their correct alphabetical
// position instead of wherever a generic/English comparison would place them.
const nameCollator = new Intl.Collator("sr", { sensitivity: "base" });

const SORT_FIELD_LABEL = {
  first_name: "Ime",
  last_name: "Prezime",
  created_at: "Datum dodavanja",
};

// created_at reads naturally as "newest/oldest first" rather than an alphabetical arrow, so its
// button arrow follows the opposite convention from the two name fields (desc = ↓ = newest
// first, matching what "sorted downward from most recent" already means everywhere else).
const sortButtonArrow = (field, dir) =>
  field === "created_at" ? (dir === "desc" ? "↓" : "↑") : dir === "asc" ? "↑" : "↓";

// Comparator factory over plain client objects ({ first_name, last_name, created_at, ... }) —
// callers `.sort()` their own list (or a list of wrapper rows, via `(a, b) =>
// compareMembersBy(field, dir)(a.client, b.client)`) with this. Last Name is the secondary sort
// for First Name (and vice versa) so members sharing a first name land in a stable,
// still-alphabetical order; Date Added has no secondary sort of its own since timestamps are
// effectively unique.
export const compareMembersBy = (field, dir) => {
  const dirMul = dir === "desc" ? -1 : 1;
  return (a, b) => {
    if (field === "created_at") {
      return (new Date(a.created_at || 0) - new Date(b.created_at || 0)) * dirMul;
    }
    const secondaryField = field === "first_name" ? "last_name" : "first_name";
    const primary = nameCollator.compare(a[field] || "", b[field] || "") * dirMul;
    if (primary !== 0) return primary;
    return nameCollator.compare(a[secondaryField] || "", b[secondaryField] || "");
  };
};

const MembersSortControl = ({ field, dir, onSelect }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const choose = (nextField, nextDir) => {
    onSelect(nextField, nextDir);
    setOpen(false);
  };

  // A plain render-helper (not a nested component definition) — declaring a component inside
  // another component's body would give React a brand-new component type on every render,
  // remounting these buttons every time `open` toggles instead of just updating them.
  const renderOption = (optField, optDir, label) => {
    const active = field === optField && dir === optDir;
    return (
      <button
        type="button"
        className={`hf-mem-sort-opt${active ? " active" : ""}`}
        onClick={() => choose(optField, optDir)}
      >
        {label}
        {active && <CheckIcon sx={{ fontSize: 14 }} />}
      </button>
    );
  };

  return (
    <div className="hf-mem-sort" ref={rootRef}>
      <button
        type="button"
        className="hf-mem-sort-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Sortiraj članove"
      >
        <SwapVertIcon sx={{ fontSize: 17 }} />
        <span>{SORT_FIELD_LABEL[field]}</span>
        <span className="hf-mem-sort-arrow">{sortButtonArrow(field, dir)}</span>
      </button>

      {open && (
        <div className="hf-mem-sort-menu" role="menu">
          <div className="hf-mem-sort-group">
            <span className="hf-mem-sort-group-label">Ime</span>
            {renderOption("first_name", "asc", "A → Š")}
            {renderOption("first_name", "desc", "Š → A")}
          </div>
          <div className="hf-mem-sort-group">
            <span className="hf-mem-sort-group-label">Prezime</span>
            {renderOption("last_name", "asc", "A → Š")}
            {renderOption("last_name", "desc", "Š → A")}
          </div>
          <div className="hf-mem-sort-group">
            <span className="hf-mem-sort-group-label">Datum dodavanja</span>
            {renderOption("created_at", "desc", "Najnoviji prvo")}
            {renderOption("created_at", "asc", "Najstariji prvo")}
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersSortControl;
